import React, { useState } from 'react';
import { Card, CardBody, Row, Col, Spinner, Alert } from 'reactstrap';
import { useDetailQuery } from 'helpers/http-client/hooks/useCrud';
import { API_ENDPOINTS } from 'helpers/http-client/httpConstants';
import { MedicineDetail } from '../types';
import { FormType } from 'components/Modals/formTypes';
import { GenericFormModal } from 'components/Modals/AddGenericModal';

interface OverviewCardProps {
  medicineId: string | undefined;
}

const OverviewCard: React.FC<OverviewCardProps> = ({ medicineId }) => {
  const {
    data: medicineDetail,
    isLoading,
    isError,
  } = useDetailQuery<MedicineDetail>(API_ENDPOINTS.MEDICINES, medicineId);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const [modalFormType, setModalFormType] = useState<FormType | null>(null);
  const handleModalSuccess = () => {
    setModalFormType(null);
  };

  const toggleModal = () => {
    setModalFormType(FormType.MEDICINE);
    console.log('Edit modal would open here');
  };

  const toggleExpand = () => setIsExpanded(!isExpanded);

  const renderField = (label: string, value: any, icon?: string) => (
    <div className="row mb-2 align-items-center" key={label}>
      <div className="col-6 text-primary fw-semibold d-flex align-items-center">
        {icon && <i className={`${icon} me-2`} />}
        <span>{label}:</span>
      </div>
      <div className="col-6">
        {typeof value === 'boolean' ? (
          value ? (
            <span className="text-success">
              <i className="fas fa-check-circle me-1" />
              Yes
            </span>
          ) : (
            <span className="text-danger">
              <i className="fas fa-times-circle me-1" />
              No
            </span>
          )
        ) : (
          value ?? 'N/A'
        )}
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
      </div>
    );
  }

  if (isError || !medicineDetail) {
    return (
      <Alert color="danger" className="text-center">
        Failed to load medicine details.
      </Alert>
    );
  }

  // Fields to show initially
  const initialFields = [
    { label: 'Name', value: medicineDetail.name, icon: 'fas fa-capsules' },
    { label: 'Alias', value: medicineDetail.alias, icon: 'fas fa-tag' },
    { label: 'Description', value: medicineDetail.description, icon: 'fas fa-info-circle' },
  ];

  // Fields to show on expansion
  const expandedFields = [
    { label: 'Dosage Form', value: medicineDetail.dosageForm, icon: 'fas fa-tablets' },
    { label: 'Strength', value: medicineDetail.strength, icon: 'fas fa-weight' },
    {
      label: 'Route of Administration',
      value: medicineDetail.routeOfAdministration,
      icon: 'fas fa-syringe',
    },
    { label: 'Medicine Type', value: medicineDetail.medicineType, icon: 'fas fa-vial' },
    {
      label: 'Storage Conditions',
      value: medicineDetail.storageConditions,
      icon: 'fas fa-thermometer',
    },
    {
      label: 'Prescription Required',
      value: medicineDetail.prescriptionRequired,
      icon: 'fas fa-file-prescription',
    },
    { label: 'Is Active', value: medicineDetail.isActive, icon: 'fas fa-toggle-on' },
    { label: 'Item Alert', value: medicineDetail.itemAlert, icon: 'fas fa-bell' },
    { label: 'Allow Due', value: medicineDetail.allowDue, icon: 'fas fa-wallet' },
    { label: 'Printable', value: medicineDetail.printable, icon: 'fas fa-print' },
    { label: 'Generate PO', value: medicineDetail.generatePO, icon: 'fas fa-file-invoice' },
    { label: 'Approval Status', value: medicineDetail.approvalStatus, icon: 'fas fa-check-double' },
    { label: 'Shelf Life (Months)', value: medicineDetail.shelfLifeMonths, icon: 'fas fa-clock' },
    { label: 'Class', value: medicineDetail.class, icon: 'fas fa-layer-group' },
  ];

  return (
    <Card
      className="shadow-sm border-0 position-relative"
      style={{ borderRadius: 8 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="p-3" style={{ background: 'linear-gradient(135deg, #e3f2fd, #bbdefb)' }}>
        <h5 className="text-primary fw-semibold mb-2">
          <i className="fas fa-capsules me-2" /> Medicine Overview
        </h5>
        <p className="text-muted mb-0">
          {medicineDetail.description || 'No description available.'}
        </p>
      </div>

      <CardBody className="pt-4">
        <Row>
          <Col sm={12}>
            {initialFields.map(({ label, value, icon }) => renderField(label, value, icon))}

            {isExpanded &&
              expandedFields.map(({ label, value, icon }) => renderField(label, value, icon))}
          </Col>
        </Row>

        <div className="mt-4 text-start">
          <button
            type="button"
            className="btn btn-primary btn-sm rounded-pill"
            onClick={toggleExpand}
          >
            {isExpanded ? 'Hide Details' : 'View Details'}{' '}
            <i className={`mdi ms-1 ${isExpanded ? 'mdi-arrow-up' : 'mdi-arrow-right'}`} />
          </button>
        </div>
      </CardBody>

      {isHovered && (
        <i
          className="fas fa-pencil-alt text-primary position-absolute"
          style={{ top: 12, right: 12, fontSize: 16, cursor: 'pointer' }}
          onClick={toggleModal}
        />
      )}

      <GenericFormModal
        isOpen={modalFormType === FormType.MEDICINE}
        toggle={() => setModalFormType(null)}
        formType={FormType.MEDICINE}
        onSuccess={handleModalSuccess}
        entityId={medicineId}
      />
    </Card>
  );
};

export default OverviewCard;
